import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Snackbar, IconButton,
  MenuItem
} from "@mui/material";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";

import { AgGridTable } from "../../Component/AgGridTable";
import { EnhancedPageTitle } from "../../Component/PageTitle";
import {
  fetchExternalConsultant, updateExternalConsultant,
  deleteExternalConsultant, fetchGetMonths,
  fetchGetYears, fetchGetProjects
} from "../../services/externalconsultantService";
import { FilterField, SearchDrawer } from "../../Component/SearchDrawer";
import CenteredCircularProgress from "../../Component/CenteredCircularProgress";
import { ConsultantData, OptionItem } from "../../Interface/Incentive";
import { AlignVerticalTop, PaymentsOutlined } from "@mui/icons-material";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>((props, ref) => (
  <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
));

const IncentivePage: React.FC = () => {
  const navigate = useNavigate();

  const [externalIncentive, setExternalIncentive] = useState<ConsultantData[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [consultantNames, setConsultantNames] = useState<string[]>([]);
  const [monthsList, setMonthsList] = useState<OptionItem[]>([]);
  const [yearsList, setYearsList] = useState<OptionItem[]>([]);
  const [projects, setProjects] = useState<OptionItem[]>([]);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [selectedRow, setSelectedRow] = useState<ConsultantData | null>(null);
  const [editedAmount, setEditedAmount] = useState("");
  const totalAmount = externalIncentive.reduce((sum, row) => {
    return sum + (row.amount || 0);
  }, 0);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

  // Load Data
  const loadData = async () => {
    try {
      setLoading(true);
      const [res, monthRes, yearRes, projectRes] = await Promise.all([
        fetchExternalConsultant(),
        fetchGetMonths(),
        fetchGetYears(),
        fetchGetProjects(),
      ]);
      const data: ConsultantData[] = res.data || [];
      const nameMap: Record<string, boolean> = {};
      const uniqueNames: string[] = [];
      data.forEach((item) => {
        const name = item.consultantName?.trim();
        if (name && !nameMap[name]) {
          nameMap[name] = true;
          uniqueNames.push(name);
        }
      });
      setConsultantNames(uniqueNames);
      setExternalIncentive(data);
      setMonthsList(monthRes.data.map((m: any) => ({ id: m.tableID, name: m.tableName })));
      setYearsList(yearRes.data.map((y: any) => ({ id: y.tableID, name: y.tableValueLong })));
      setProjects(projectRes.data.map((p: any) => ({ id: p.projectId, name: p.projectName })));
    } catch (error) {
      console.error("Load error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handle Edit Click
  const handleEditClick = (row: ConsultantData) => {
    setSelectedRow(row);
    setEditedAmount(row.amount.toString());
    setEditDialogOpen(true);
  };

  // Handle Delete Click
  const handleDeleteClick = (row: ConsultantData) => {
    setSelectedRow(row);
    setDeleteDialogOpen(true);
  };

  // Handle Search
  const handleSearch = async (filters: Record<string, string>) => {
    try {
      setLoading(true);
      const res = await fetchExternalConsultant(filters);
      setExternalIncentive(res.data || []);
      setDrawerOpen(false);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setLoading(false);
    }
  };

  // Confirm Edit
  const confirmEdit = async () => {
    if (!selectedRow) return;
    const monthId = monthsList.find(m => m.name.toLowerCase().trim() === selectedRow.month.toLowerCase().trim())?.id;
    const yearId = yearsList.find(y => y.name.toLowerCase().trim() === selectedRow.year.toLowerCase().trim())?.id;
    const projectId = projects.find(p => p.name.toLowerCase().trim() === selectedRow.projectName.toLowerCase().trim())?.id;
    if (!monthId || !yearId || !projectId) {
      console.warn("Invalid mapping for month/year/project");
      return;
    }
    const payload = {
      consultantId: selectedRow.consultantId,
      consultantName: selectedRow.consultantName,
      amount: parseFloat(editedAmount),
      monthId,
      yearId,
      projectId,
      modifiedOn: new Date().toISOString(),
      modifiedBy: 1,
    };
    try {
      const response = await updateExternalConsultant(payload);
      if (response?.success) {
        setSnackbarMessage("Updated Successfully!");
        setSnackbarSeverity("success");
        await loadData();
      } else {
        setSnackbarMessage(response?.message || "Update failed!");
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error("Update failed:", error);
      setSnackbarMessage("Update failed due to server error!");
      setSnackbarSeverity("error");
    }
    setSnackbarOpen(true);
    setEditDialogOpen(false);
  };

  // Confirm Delete
  const confirmDelete = async () => {
    if (!selectedRow) return;
    try {
      const response = await deleteExternalConsultant(selectedRow.consultantId);
      if (response.data === "Consultant is deleted Successfully.") {
        setSnackbarMessage("Deleted Successfully!");
        setSnackbarSeverity("success");
        await loadData();
      } else {
        setSnackbarMessage(response?.message || "Delete not allowed. Invoice is open.");
        setSnackbarSeverity("error");
      }
    } catch (error: any) {
      console.error("Delete failed:", error);
      setSnackbarMessage(
        error?.response?.data?.message || "Delete failed due to server error!"
      );
      setSnackbarSeverity("error");
    }
    setSnackbarOpen(true);
    setDeleteDialogOpen(false);
  };

  // Fields for Search Drawer
  const filterFields: FilterField[] = useMemo(() => [
    {
      key: "consultantName",
      label: "Consultant Name",
      type: "dropdown",
      options: consultantNames,
    },
    {
      key: "projectName",
      label: "Project",
      type: "dropdown",
      options: projects.map(p => p.name),
    },
    {
      key: "month",
      label: "Month",
      type: "dropdown",
      options: monthsList.map(m => m.name),
    },
    {
      key: "year",
      label: "Year",
      type: "dropdown",
      options: yearsList.map(y => y.name?.toString() ?? ""),
    },
  ], [consultantNames, projects, monthsList, yearsList]);

  return (
    <Box sx={{ padding: 3, backgroundColor: "rgb(238, 242, 246)" }}>
      <EnhancedPageTitle
        title={`Incentive Dashboard - ₹${totalAmount.toLocaleString("en-IN")}`}
        icon={<PaymentsOutlined />}
        button1Text="Add Incentive"
        button1Action={() => navigate("/AddIncentive")}
        button2Text="Search"
        button2Action={() => setDrawerOpen(true)}
      />
      {loading ? <CenteredCircularProgress /> : (
        <>
          <SearchDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            filterFields={filterFields}
            onSearch={handleSearch}
            onReset={loadData}
          />

          <AgGridTable
            domLayout="autoHeight"
            customStyles="legacy"
            rowSelection="single"
            gridOptions={{}}
            columns={[
              {
                field: "consultantName",
                headerName: "Consultant",
                filter: true,
              },
              {
                field: "amount",
                headerName: "Amount",
                filter: true,
                cellStyle: { textAlign: "right" },
                minWidth: 200,
                maxWidth: 260,
                flex: 1,
                valueFormatter: (params: any) =>
                  `₹ ${parseFloat(params.value || 0).toLocaleString("en-IN")}`,
              },
              {
                field: "month",
                headerName: "Month",
                filter: true,
                minWidth: 100,
                maxWidth: 150,
                flex: 1,
                cellStyle: { textAlign: "center" },
              },
              {
                field: "year",
                headerName: "Year",
                filter: true,
                minWidth: 100,
                maxWidth: 150,
                flex: 1,
                cellStyle: { textAlign: "center" },
              },
              {
                field: "projectName",
                headerName: "Project",
                filter: true,
                minWidth: 150,
                maxWidth: 200,
                flex: 1,
              },
              {
                field: "actions",
                headerName: "Actions",
                minWidth: 100,
                maxWidth: 130,
                cellRenderer: (params: any) => (
                  <>
                    <IconButton onClick={() => handleEditClick(params.data)}>
                      <EditIcon color="primary" />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteClick(params.data)}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  </>
                ),
              },
            ]}
            data={externalIncentive}
            handleSelectedRows={() => { }}
            noDataComponentText="No Incentive data available"
            pagination={false}
            paginationPerPage={10}
          />

          {/* Edit Dialog */}
          <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Edit Incentive</DialogTitle>
            <DialogContent>
              <TextField
                label="Name"
                fullWidth
                margin="normal"
                value={selectedRow?.consultantName ?? ""}
                onChange={(e) => setSelectedRow(prev => prev ? { ...prev, consultantName: e.target.value } : null)}
              />
              <TextField
                select
                label="Project"
                fullWidth
                margin="normal"
                value={selectedRow?.projectName ?? ""}
                onChange={(e) => setSelectedRow(prev => prev ? { ...prev, projectName: e.target.value } : null)}
              >
                {projects.map((proj) => (
                  <MenuItem key={proj.id} value={proj.name}>
                    {proj.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Month"
                fullWidth
                margin="normal"
                value={selectedRow?.month ?? ""}
                onChange={(e) => setSelectedRow(prev => prev ? { ...prev, month: e.target.value } : null)}
              >
                {monthsList.map((m) => (
                  <MenuItem key={m.id} value={m.name}>
                    {m.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Year"
                fullWidth
                margin="normal"
                value={selectedRow?.year ?? ""}
                onChange={(e) => setSelectedRow(prev => prev ? { ...prev, year: e.target.value } : null)}
              >
                {yearsList.map((y) => (
                  <MenuItem key={y.id} value={y.name}>
                    {y.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Amount"
                fullWidth
                margin="normal"
                type="number"
                value={editedAmount}
                onChange={(e) => setEditedAmount(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={confirmEdit} variant="contained" color="primary">Update</Button>
              <Button onClick={() => setEditDialogOpen(false)} variant="outlined">Back</Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirm Dialog */}
          <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              Are you sure you want to delete <b>{selectedRow?.consultantName}</b>'s incentive?
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button onClick={confirmDelete} color="error">Delete</Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert
              onClose={() => setSnackbarOpen(false)}
              severity={snackbarSeverity}
              sx={{ width: "100%" }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </>
      )}
    </Box>
  );
};

export default IncentivePage;
